// Libs
import classNames from 'classnames/bind';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLoadScript } from '@react-google-maps/api';
// Components, Layouts, Pages
import { BaseInput, BaseInputAddress, BaseMap } from '~/components/common';
// Others
import { IAddressJob } from '~/utils/interface/job';
import { defaultValuesForm, schema } from './helper';
import { IAddressCustomerOption } from '~/utils/interface/customer';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, EMPTY_STRING } from '~/utils/constants/common';
import { IBaseAddress } from '~/utils/interface/common';
import { googleMapApiKey } from '~/utils/constants/env';
// Styles, images, icons
import styles from './ModalEditAddress.module.scss';

type Props = {
  onClose: () => void;
  data?: IAddressCustomerOption | null;
  onChange: (data: IAddressJob) => void;
};

const cx = classNames.bind(styles);

const ModalEditAddress = (props: Props) => {
  //#region Destructuring Props
  const { data, onClose, onChange } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'common/base']);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IAddressJob>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });

  const latitude = useWatch({ control, name: 'latitude' });
  const longitude = useWatch({ control, name: 'longitude' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data) return;
    reset(data);
  }, [data, reset]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleCancelModalEditAddress = () => {
    reset(defaultValuesForm);
    onClose();
  };

  const handleSubmitModalEditAddress = (data: IAddressJob) => {
    onChange(data);
    handleCancelModalEditAddress();
  };

  const handleSelectAddress = (value: IBaseAddress) => {
    const { address, city, state, zipCode, country } = value;

    setValue('address', address || EMPTY_STRING, { shouldValidate: true });
    setValue('city', city || EMPTY_STRING, { shouldValidate: true });
    setValue('state', state || EMPTY_STRING, { shouldValidate: true });
    setValue('zipCode', zipCode || EMPTY_STRING, { shouldValidate: true });
    setValue('country', country || EMPTY_STRING, { shouldValidate: true });
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='modalEditAddress'
      open={true}
      fullWidth
      maxWidth={'md'}
      onClose={handleCancelModalEditAddress}
    >
      <Box component={'form'} className={cx('form')}>
        <DialogTitle>{t('form_edit_address_job_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={6} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={12}>
                <Controller
                  name='address'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInputAddress
                      required
                      label={t('form_edit_address_job_address_label')}
                      value={value}
                      onChange={onChange}
                      onSelectAddress={handleSelectAddress}
                      errorMessage={errors?.address?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={12}>
                <Controller
                  name='address2'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_edit_address_job_second_address_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.address2?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={12} container columnSpacing={'12px'}>
                <Grid2 size={6}>
                  <Controller
                    name='city'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_edit_address_job_city_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.city?.message}
                        required
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='state'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_edit_address_job_state_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.state?.message}
                        required
                      />
                    )}
                  />
                </Grid2>
              </Grid2>

              <Grid2 size={12} container columnSpacing={'12px'}>
                <Grid2 size={6}>
                  <Controller
                    name='zipCode'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_edit_address_job_zip_code_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.zipCode?.message}
                        required
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='country'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_edit_address_job_country_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.country?.message}
                        required
                      />
                    )}
                  />
                </Grid2>
              </Grid2>
            </Grid2>

            <Grid2 container size={6} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Box component={'div'} className={cx('map')}>
                {isLoaded && (
                  <BaseMap
                    {...(latitude &&
                      longitude && {
                        position: {
                          lat: latitude as number,
                          lng: longitude as number,
                        },
                      })}
                  />
                )}
              </Box>
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCancelModalEditAddress}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button
              variant='contained'
              type='button'
              disabled={data ? !isDirty : false}
              onClick={handleSubmit(handleSubmitModalEditAddress)}
            >
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ModalEditAddress;
